// Inicialización singleton de Firebase Web SDK.
// Se importa desde main.tsx ANTES de renderizar la app para que Analytics
// arranque tan pronto como sea posible y capture la primera page view.
//
// La config viene del bundle (inyectada por vite.config.ts vía `define`).
// Los valores son IDs públicos diseñados para exponerse en cliente —
// la seguridad de Firebase está en sus reglas de servidor, no en ocultarlos.
//
// Si por algún motivo la config no está completa (variables vacías), las
// llamadas no fallan: simplemente no se inicializa nada y los helpers
// son no-op. Esto facilita correr la app en local sin Firebase.
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics';

let app: FirebaseApp | null = null;
let analytics: Analytics | null = null;

function configIsValid(): boolean {
  const c = __FIREBASE_CONFIG__;
  return !!(c.apiKey && c.projectId && c.appId);
}

// Detecta si el host actual es una IP literal o localhost. En esos casos
// Google Analytics intenta setear cookies que el navegador rechaza con
// "invalid domain" (las IPs no pueden ser eTLD+1). Las llamadas a GA no
// fallan, pero ensucian la consola con warnings. Como en esos hosts las
// métricas no llegan a GA igualmente (dominio no autorizado), evitamos
// inicializar Analytics y ahorramos el ruido.
function shouldDisableAnalytics(): boolean {
  if (typeof window === 'undefined') return true;
  const host = window.location.hostname;
  if (!host) return false;
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return true;
  // IPv4 (192.168.x.x, 10.x.x.x, etc.)
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return true;
  // IPv6 literal entre brackets, ej. "[fe80::1]"
  if (host.startsWith('[') && host.endsWith(']')) return true;
  return false;
}

export async function initFirebase(): Promise<void> {
  if (app) return;
  if (!configIsValid()) return;
  app = initializeApp(__FIREBASE_CONFIG__);
  // Analytics solo se carga en navegadores compatibles (no en SSR/SW) y
  // cuando el host es un dominio real — evitamos el spam de cookies
  // rechazadas al acceder via IP/localhost.
  if (shouldDisableAnalytics()) return;
  try {
    if (await isSupported()) {
      analytics = getAnalytics(app);
    }
  } catch { /* analytics no disponible — seguimos sin él */ }
}

export function getFirebaseAnalytics(): Analytics | null {
  return analytics;
}

// La app inicializada, para los servicios que necesitan otro producto de
// Firebase (la telemetría en vivo usa Realtime Database y auth anónima).
// null si la config no está completa — el caller debe tratarlo como "sin
// Firebase" y seguir funcionando.
export function getFirebaseApp(): FirebaseApp | null {
  return app;
}
